class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    // Only validate actual quantity inputs, not other form elements
    if (!event.target.classList.contains('quantity__input') && !event.target.hasAttribute('data-quantity-variant-id')) {
      return;
    }

    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          
          // Update the entire cart drawer content to ensure upsell sections are properly refreshed
          const newDrawer = html.querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
            // Re-initialize all button handlers after content replacement
            requestAnimationFrame(() => {
              CartItems.setupCompleteSetButtons();
              CartItems.setupStrawberryMilkButtons();
              CartItems.setupSweatpantUpsellButtons();
              CartItems.setupSweatpantUpsellSliderButtons();
              CartItems.updateShippingDisplay();
              ShippingProtection.setupCheckoutButton();
              ShippingProtection.setupNoProtectionButton();
            });
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  initializeAllButtons() {
    // Use requestAnimationFrame to ensure DOM is fully updated before initializing buttons
    requestAnimationFrame(() => {
      CartItems.setupCompleteSetButtons();
      CartItems.setupStrawberryMilkButtons();
      CartItems.setupSweatpantUpsellButtons();
      CartItems.setupSweatpantUpsellSliderButtons();
    });
  }

  static setupCompleteSetButtons() {
    // Remove any existing listeners to prevent duplicates
    document.querySelectorAll('.complete-set-btn').forEach(button => {
      button.replaceWith(button.cloneNode(true));
    });

    // Add event listeners to Complete the Set buttons
    document.querySelectorAll('.complete-set-btn').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        const parent = button.closest('.auto_product_main');
        const variantSelect = parent.querySelector('select');
        const variantId = variantSelect.value;

        if (!variantId) {
          alert('Please select a size before adding to cart.');
          return;
        }

        button.disabled = true;
        button.textContent = "Adding...";

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        })
        .then(() => fetch(window.location.pathname + '?section_id=cart-drawer'))
        .then(res => res.text())
        .then(html => {
          const newDrawer = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
            // Re-initialize all buttons after content replacement
            requestAnimationFrame(() => {
              CartItems.setupCompleteSetButtons();
              CartItems.setupStrawberryMilkButtons();
              CartItems.setupSweatpantUpsellButtons();
              CartItems.updateShippingDisplay();
            });
          }
        })
        .finally(() => {
          button.disabled = false;
          button.textContent = "Complete the Set";
        });
      });
    });
  }

  static setupStrawberryMilkButtons() {
    // Remove any existing listeners to prevent duplicates
    document.querySelectorAll('.strawberry-milk-add-btn').forEach(button => {
      button.replaceWith(button.cloneNode(true));
    });

    // Add event listeners to strawberry milk add to cart buttons
    document.querySelectorAll('.strawberry-milk-add-btn').forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        const productTitle = this.getAttribute('data-product-title');
        const productItem = this.closest('.strawberry-milk-product-item');
        const sizeSelect = productItem.querySelector('.size-select');
        const variantId = sizeSelect.value;
        
        // Clear any existing error styling
        sizeSelect.classList.remove('error');
        
        if (!variantId) {
          // Show warning and highlight the size selector
          sizeSelect.classList.add('error');
          alert('Please select a size before adding to cart.');
          sizeSelect.focus();
          return;
        }

        // Disable button and show loading state
        const originalText = this.textContent;
        this.disabled = true;
        this.textContent = 'Adding...';
        
        // Add to cart using Shopify's cart API
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }
          return response.json();
        })
        .then(data => {
          // Refresh the cart drawer content
          return fetch(`${window.location.pathname}?section_id=cart-drawer`);
        })
        .then(response => response.text())
        .then(html => {
          const newDrawer = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
            // Re-initialize all button handlers after content replacement
            requestAnimationFrame(() => {
              CartItems.setupCompleteSetButtons();
              CartItems.setupStrawberryMilkButtons();
              CartItems.setupSweatpantUpsellButtons();
              CartItems.setupSweatpantUpsellSliderButtons();
              CartItems.updateShippingDisplay();
              ShippingProtection.setupCheckoutButton();
              ShippingProtection.setupNoProtectionButton();
            });
          }
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          // Restore button state on error
          this.disabled = false;
          this.textContent = originalText;
          alert('Sorry, there was an error adding this item to your cart. Please try again.');
        });
      });
    });
  }

  static setupSweatpantUpsellButtons() {
    // Remove any existing listeners to prevent duplicates
    document.querySelectorAll('.sweatpant-upsell-add-btn').forEach(button => {
      button.replaceWith(button.cloneNode(true));
    });

    // Add event listeners to sweatpant upsell add to cart buttons
    document.querySelectorAll('.sweatpant-upsell-add-btn').forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        const productTitle = this.getAttribute('data-product-title');
        const upsellContainer = this.closest('.sweatpant-item-upsell') || this.closest('.sweatpant-upsell');
        const sizeSelect = upsellContainer.querySelector('.sweatpant-upsell-variant-select');
        const variantId = sizeSelect.value;
        
        // Clear any existing error styling
        sizeSelect.classList.remove('error');
        
        if (!variantId) {
          // Show warning and highlight the size selector
          sizeSelect.classList.add('error');
          alert('Please select a size before adding to cart.');
          sizeSelect.focus();
          return;
        }

        // Disable button and show loading state
        const originalText = this.textContent;
        this.disabled = true;
        this.textContent = 'Adding...';
        
        // Add to cart using Shopify's cart API
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }
          return response.json();
        })
        .then(data => {
          // Refresh the cart drawer content
          return fetch(`${window.location.pathname}?section_id=cart-drawer`);
        })
        .then(response => response.text())
        .then(html => {
          const newDrawer = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
            // Re-initialize all button handlers after content replacement
            requestAnimationFrame(() => {
              CartItems.setupCompleteSetButtons();
              CartItems.setupStrawberryMilkButtons();
              CartItems.setupSweatpantUpsellButtons();
              CartItems.setupSweatpantUpsellSliderButtons();
              CartItems.updateShippingDisplay();
              ShippingProtection.setupCheckoutButton();
              ShippingProtection.setupNoProtectionButton();
            });
          }
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          // Restore button state on error
          this.disabled = false;
          this.textContent = originalText;
          alert('Sorry, there was an error adding this item to your cart. Please try again.');
        });
      });
    });
  }

  static setupSweatpantUpsellSliderButtons() {
    // Remove any existing listeners to prevent duplicates
    document.querySelectorAll('.sweatpant-slider-add-btn').forEach(button => {
      button.replaceWith(button.cloneNode(true));
    });

    // Add event listeners to sweatpant slider add to cart buttons
    document.querySelectorAll('.sweatpant-slider-add-btn').forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        const productTitle = this.getAttribute('data-product-title');
        const productItem = this.closest('.sweatpant-slider-product-item');
        const sizeSelect = productItem.querySelector('.sweatpant-slider-size-select');
        const variantId = sizeSelect.value;
        
        // Clear any existing error styling
        sizeSelect.classList.remove('error');
        
        if (!variantId) {
          // Show warning and highlight the size selector
          sizeSelect.classList.add('error');
          alert('Please select a size before adding to cart.');
          sizeSelect.focus();
          return;
        }

        // Disable button and show loading state
        const originalText = this.textContent;
        this.disabled = true;
        this.textContent = 'Adding...';
        
        // Add to cart using Shopify's cart API
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }
          return response.json();
        })
        .then(data => {
          // Refresh the cart drawer content
          return fetch(`${window.location.pathname}?section_id=cart-drawer`);
        })
        .then(response => response.text())
        .then(html => {
          const newDrawer = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
            // Re-initialize all button handlers after content replacement
            requestAnimationFrame(() => {
              CartItems.setupCompleteSetButtons();
              CartItems.setupStrawberryMilkButtons();
              CartItems.setupSweatpantUpsellButtons();
              CartItems.setupSweatpantUpsellSliderButtons();
              CartItems.updateShippingDisplay();
              ShippingProtection.setupCheckoutButton();
              ShippingProtection.setupNoProtectionButton();
            });
          }
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          // Restore button state on error
          this.disabled = false;
          this.textContent = originalText;
          alert('Sorry, there was an error adding this item to your cart. Please try again.');
        });
      });
    });
  }

  static updateShippingDisplay() {
    // Function to dynamically check for combo cart condition and update shipping display
    fetch('/cart.js')
      .then(response => response.json())
      .then(cartData => {
        const cartItems = cartData.items;
        let hasCombo = false;

        console.log('Checking cart items for combo:', cartItems.map(item => ({ title: item.product_title, id: item.product_id })));

        // Check for saga zip-up and corresponding upsell combinations
        const sagaCombos = [
          { trigger: 'Black Saga', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product5 },
          { trigger: 'Crème Saga', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product6 },
          { trigger: 'Gray Saga', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product7 },
          { trigger: 'Pink Saga', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product8 },
          { trigger: 'Blue Saga', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product9 },
          { trigger: 'Black Sadboy Medieval', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product10 },
          { trigger: 'Gray Medieval', triggerKeyword: 'Zip', upsellId: window.cartDrawerSettings?.auto_product11 }
        ];

        console.log('Saga combos to check:', sagaCombos);

        // Check each combo
        for (const combo of sagaCombos) {
          if (!combo.upsellId) continue;
          
          const hasTrigger = cartItems.some(item => 
            item.product_title.includes(combo.trigger) && 
            item.product_title.includes(combo.triggerKeyword)
          );
          
          const hasUpsell = cartItems.some(item => 
            item.product_id === combo.upsellId
          );
          
          console.log(`Checking combo ${combo.trigger}: trigger=${hasTrigger}, upsell=${hasUpsell}`);
          
          if (hasTrigger && hasUpsell) {
            hasCombo = true;
            console.log('Found matching combo!');
            break;
          }
        }

        // Check for sweatpant combinations if no saga combo found
        if (!hasCombo) {
          // Look for sweatpant trigger items and their corresponding upsells
          const sweatpantTriggers = cartItems.filter(item => 
            item.product_title.toLowerCase().includes('sweatpant')
          );
          
          console.log('Checking sweatpant combos, triggers found:', sweatpantTriggers.length);
          
          for (const triggerItem of sweatpantTriggers) {
            // Check if there's a corresponding upsell for this sweatpant
            const hasCorrespondingUpsell = cartItems.some(upsellItem => 
              upsellItem.product_id !== triggerItem.product_id &&
              (upsellItem.product_title.toLowerCase().includes('sweatpant') ||
               upsellItem.product_title.includes(triggerItem.product_title.split(' ')[0])) // Match color/style
            );
            
            if (hasCorrespondingUpsell) {
              hasCombo = true;
              console.log('Found sweatpant combo!');
              break;
            }
          }
        }

        console.log('Final combo status:', hasCombo);

        // Update shipping display based on combo status
        const shippingCustomDiv = document.querySelector('.shipping_custom');
        if (shippingCustomDiv) {
          if (hasCombo) {
            shippingCustomDiv.style.color = 'green';
            console.log('Shipping display updated: SHOWN and GREEN');
          } else {
            shippingCustomDiv.style.display = 'none';
            console.log('Shipping display updated: HIDDEN');
          }
        } else {
          console.log('No .shipping_custom div found');
        }
      })
      .catch(error => {
        console.error('Error checking cart for combo:', error);
      });
  }

  static initializeButtonObserver() {
    // Create a MutationObserver to watch for new upsell buttons being added to the DOM
    const observer = new MutationObserver(function(mutations) {
      let shouldReinitialize = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          // Check if any added nodes contain upsell buttons
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const hasCompleteSetButtons = node.querySelector && node.querySelector('.complete-set-btn');
                const hasStrawberryMilkButtons = node.querySelector && node.querySelector('.strawberry-milk-add-btn');
                const hasSweatpantButtons = node.querySelector && node.querySelector('.sweatpant-upsell-add-btn');
                const hasSweatpantSlider = node.querySelector && node.querySelector('.sweatpant-upsell-slider');
                const hasSweatpantSliderButtons = node.querySelector && node.querySelector('.sweatpant-slider-add-btn');
                
                if (hasCompleteSetButtons || hasStrawberryMilkButtons || hasSweatpantButtons || hasSweatpantSlider || hasSweatpantSliderButtons) {
                  shouldReinitialize = true;
                }
              }
            });
        }
      });
      
      if (shouldReinitialize) {
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          console.log('MutationObserver detected new upsell buttons, reinitializing...');
          CartItems.setupCompleteSetButtons();
          CartItems.setupStrawberryMilkButtons();
          CartItems.setupSweatpantUpsellButtons();
          CartItems.setupSweatpantUpsellSliderButtons();
          CartItems.updateShippingDisplay();
          ShippingProtection.setupCheckoutButton();
          ShippingProtection.setupNoProtectionButton();
        });
      }
    });
    
    // Start observing the cart drawer for changes
    const cartDrawer = document.querySelector('#CartDrawer');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
      console.log('MutationObserver initialized for cart drawer upsell buttons');
    }
    
    // Also observe the document body in case cart drawer is replaced entirely
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    console.log('MutationObserver initialized for upsell buttons');
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // For cart drawer, refresh the entire drawer content to ensure upsells are properly updated
        if (this.tagName === 'CART-DRAWER-ITEMS') {
          fetch(`${routes.cart_url}?section_id=cart-drawer`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const newDrawer = html.querySelector('#CartDrawer');
              const currentDrawer = document.querySelector('#CartDrawer');
              if (newDrawer && currentDrawer) {
                currentDrawer.innerHTML = newDrawer.innerHTML;
                // Re-initialize all buttons after content replacement
                requestAnimationFrame(() => {
                  CartItems.setupCompleteSetButtons();
                  CartItems.setupStrawberryMilkButtons();
                  CartItems.setupSweatpantUpsellButtons();
                  CartItems.updateShippingDisplay();
                });
              }
            })
            .catch((e) => {
              console.error(e);
            });
        } else {
          // For regular cart, update sections normally
          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Upsell Management functionality
class UpsellManager {
  static getSelectedUpsells() {
    const selectedUpsells = [];
    
    // Check Extended Warranty
    const extendedWarrantyButton = document.querySelector('.extended-warranty-pinned .product_added.active');
    if (extendedWarrantyButton && extendedWarrantyButton.style.display !== 'none') {
      const variantId = extendedWarrantyButton.getAttribute('variant_id');
      if (variantId) {
        selectedUpsells.push({
          id: variantId,
          quantity: 1,
          type: 'extended_warranty'
        });
      }
    }
    
    // Check Essential Laces
    const essentialLacesButton = document.querySelector('.essential-laces-pinned .product_added.active');
    if (essentialLacesButton && essentialLacesButton.style.display !== 'none') {
      const variantId = essentialLacesButton.getAttribute('variant_id');
      if (variantId) {
        selectedUpsells.push({
          id: variantId,
          quantity: 1,
          type: 'essential_laces'
        });
      }
    }
    
    return selectedUpsells;
  }
  
  static async addUpsellsToCart(upsells) {
    if (upsells.length === 0) {
      return true;
    }
    
    try {
      // Prepare items for bulk add
      const items = upsells.map(upsell => ({
        id: upsell.id,
        quantity: upsell.quantity
      }));
      
      console.log('Adding upsells to cart:', items);
      
      // Use bulk add to cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        console.error('Failed to add upsells to cart:', response.statusText);
        return false;
      }
      
      const result = await response.json();
      console.log('Upsells added successfully:', result);
      return true;
      
    } catch (error) {
      console.error('Error adding upsells to cart:', error);
      return false;
    }
  }
}

// Shipping Protection functionality
class ShippingProtection {
  static SHIPPING_PROTECTION_HANDLE = 'shipping-protection';
  
  static async addShippingProtection() {
    try {
      // First, check if shipping protection is already in cart
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      const hasShippingProtection = cartData.items.some(item => 
        item.handle === this.SHIPPING_PROTECTION_HANDLE ||
        item.product_title.toLowerCase().includes('shipping protection') ||
        item.product_title.toLowerCase().includes('package protection')
      );
      
      if (hasShippingProtection) {
        console.log('Shipping protection already in cart');
        return true;
      }
      
      // Try to get the shipping protection product
      const productResponse = await fetch(`/products/${this.SHIPPING_PROTECTION_HANDLE}.js`);
      
      if (!productResponse.ok) {
        console.log('Shipping protection product not found');
        return false;
      }
      
      const productData = await productResponse.json();
      const variantId = productData.variants[0]?.id;
      
      if (!variantId) {
        console.log('No variant found for shipping protection');
        return false;
      }
      
      // Add shipping protection to cart
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      
      if (!addResponse.ok) {
        console.log('Failed to add shipping protection to cart');
        return false;
      }
      
      console.log('Shipping protection added to cart');
      return true;
      
    } catch (error) {
      console.error('Error adding shipping protection:', error);
      return false;
    }
  }
  
  static async removeShippingProtection() {
    try {
      // Get current cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Find shipping protection items in cart
      const shippingProtectionItems = cartData.items.filter(item => 
        item.handle === this.SHIPPING_PROTECTION_HANDLE ||
        item.product_title.toLowerCase().includes('shipping protection') ||
        item.product_title.toLowerCase().includes('package protection')
      );
      
      if (shippingProtectionItems.length === 0) {
        console.log('No shipping protection items found in cart');
        return true;
      }
      
      // Remove each shipping protection item
      for (let i = 0; i < shippingProtectionItems.length; i++) {
        const item = shippingProtectionItems[i];
        // Find the line number for this item in the cart
        const lineIndex = cartData.items.findIndex(cartItem => cartItem.key === item.key);
        if (lineIndex === -1) continue;
        
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndex + 1, // Shopify uses 1-based line numbers
            quantity: 0
          })
        });
        
        if (!removeResponse.ok) {
          console.error(`Failed to remove shipping protection item: ${item.product_title}`);
          return false;
        }
      }
      
      console.log('Shipping protection removed from cart');
      return true;
      
    } catch (error) {
      console.error('Error removing shipping protection:', error);
      return false;
    }
  }
  
  static setupCheckoutButton() {
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    if (!checkoutButton) return;
    
    // Remove any existing listeners
    const newButton = checkoutButton.cloneNode(true);
    checkoutButton.parentNode.replaceChild(newButton, checkoutButton);
    
    // Add new event listener
    newButton.addEventListener('click', async function(e) {
      e.preventDefault();
      
      // Show loading state
      const originalText = this.textContent;
      this.disabled = true;
      
      try {
        // Check for selected upsells first
        const selectedUpsells = UpsellManager.getSelectedUpsells();
        console.log('Selected upsells for checkout+:', selectedUpsells);
        
        // Add upsells to cart if any are selected
        if (selectedUpsells.length > 0) {
          const upsellSuccess = await UpsellManager.addUpsellsToCart(selectedUpsells);
          if (!upsellSuccess) {
            console.warn('Failed to add some upsells, continuing with checkout');
          }
        }
        
        // Add shipping protection
        await ShippingProtection.addShippingProtection();
        
        // Set timeout to revert text after 2 seconds
        setTimeout(() => {
          this.textContent = originalText;
          this.disabled = false;
        }, 1000);
        
        // Small delay to ensure cart is updated
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Redirect directly to checkout page
        window.location.href = '/checkout';
        
      } catch (error) {
        console.error('Error during checkout process:', error);
        
        // Proceed to checkout anyway even if items failed to add
        window.location.href = '/checkout';
      }
    });
  }
  
  static setupNoProtectionButton() {
    const noProtectionButton = document.querySelector('#CartDrawer-NoProtection');
    if (!noProtectionButton) return;
    
    // Remove any existing listeners
    const newButton = noProtectionButton.cloneNode(true);
    noProtectionButton.parentNode.replaceChild(newButton, noProtectionButton);
    
    // Add new event listener that removes shipping protection and goes to checkout
    newButton.addEventListener('click', async function(e) {
      e.preventDefault();
      
      // Show loading state
      const originalText = this.textContent;
      this.disabled = true;
      
      try {
        // Check for selected upsells first
        const selectedUpsells = UpsellManager.getSelectedUpsells();
        console.log('Selected upsells for regular checkout:', selectedUpsells);
        
        // Add upsells to cart if any are selected
        if (selectedUpsells.length > 0) {
          const upsellSuccess = await UpsellManager.addUpsellsToCart(selectedUpsells);
          if (!upsellSuccess) {
            console.warn('Failed to add some upsells, continuing with checkout');
          }
          
          // Small delay to ensure cart is updated
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        // Remove shipping protection if it exists in the cart
        await ShippingProtection.removeShippingProtection();
        
        // Set timeout to revert text after 2 seconds
        setTimeout(() => {
          this.textContent = originalText;
          this.disabled = false;
        }, 1000);
        
        // Small delay to ensure cart is updated
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Redirect to checkout
        window.location.href = '/checkout';
        
      } catch (error) {
        console.error('Error during checkout process:', error);
        
        // Proceed to checkout anyway even if operations failed
        window.location.href = '/checkout';
      }
    });
  }
}

// Initialize all buttons when the page loads
document.addEventListener('DOMContentLoaded', function() {
  CartItems.setupCompleteSetButtons();
  CartItems.setupStrawberryMilkButtons();
  CartItems.setupSweatpantUpsellButtons();
  CartItems.setupSweatpantUpsellSliderButtons();
  CartItems.updateShippingDisplay();
  
  // Set up shipping protection for checkout button
  ShippingProtection.setupCheckoutButton();
  
  // Set up no protection button
  ShippingProtection.setupNoProtectionButton();
  
  // Set up MutationObserver to automatically initialize buttons when they're added to the DOM
  CartItems.initializeButtonObserver();
});
